import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { LogIn, Loader2 } from "lucide-react";
import { GoogleAuthButton } from "@/components/GoogleAuthButton";

export default function Login() {
  const { login } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const loc = useLocation();
  const [sp] = useSearchParams();
  const from = (() => {
    const q = sp.get("next");
    if (q && q.startsWith("/") && !q.startsWith("//")) return q;
    return loc.state?.from || "/";
  })();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (sp.get("err") === "google") {
      setErr(t("auth.google_error"));
    }
  }, [sp, t]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) nav(from, { replace: true });
    else setErr(res.error);
  };

  return (
    <div className="max-w-md mx-auto py-6">
      <div className="bg-white border-2 border-[#E4DDD3] rounded-2xl p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-[#C85A32] text-white flex items-center justify-center">
            <LogIn className="w-5 h-5" />
          </div>
          <h1 className="text-2xl font-bold">{t("auth.welcome_back")}</h1>
        </div>
        <p className="text-[#4A525A] mb-6">{t("auth.sign_in_desc")}</p>

        <GoogleAuthButton testid="login-google" />

        <div className="my-5 flex items-center gap-3 text-xs text-[#707880]">
          <span className="flex-1 h-px bg-[#E4DDD3]" /> {t("auth.or")} <span className="flex-1 h-px bg-[#E4DDD3]" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          <Field label={t("auth.email")}>
            <input
              data-testid="login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-xl border-2 border-[#E4DDD3] focus:border-[#C85A32] focus:ring-4 focus:ring-[#C85A32]/20 outline-none p-3 bg-white"
              autoComplete="email"
            />
          </Field>
          <Field label={t("auth.password")}>
            <input
              data-testid="login-password"
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-xl border-2 border-[#E4DDD3] focus:border-[#C85A32] focus:ring-4 focus:ring-[#C85A32]/20 outline-none p-3 bg-white"
              autoComplete="current-password"
            />
          </Field>

          {err && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="login-error">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="pod-focus w-full bg-[#C85A32] hover:bg-[#B24C28] disabled:opacity-70 text-white font-bold py-3 px-6 rounded-xl inline-flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
            {t("auth.sign_in")}
          </button>
        </form>

        <div className="mt-5 text-sm text-[#4A525A]">
          {t("auth.new_here")}{" "}
          <Link to="/register" data-testid="login-goto-register" className="font-bold text-[#C85A32] pod-focus">
            {t("auth.create_account")}
          </Link>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-[#707880] font-semibold">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
